import { io } from 'socket.io-client';

/**
 * ScribeSocketClient
 * Socket.IO client for the /scribe namespace (ambient scribe sessions).
 * Follows the same pattern as voiceSocketClient.js.
 */
export class ScribeSocketClient {
  constructor() {
    this.socket = null;
    this.handlers = {};
    this.isConnected = false;
  }

  connect({ token, url } = {}) {
    if (this.socket) {
      this.disconnect();
    }

    // In dev, CRA runs on :3000 but the API/Socket.IO server is on :3001
    const serverUrl = url || window.location.origin.replace(':3000', ':3001');
    this.socket = io(`${serverUrl}/scribe`, {
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000
    });

    const events = [
      'scribeReady', 'scribeTranscript', 'scribeStatus',
      'scribeNotesReady', 'scribeError', 'scribeSessionClosed'
    ];

    for (const event of events) {
      this.socket.on(event, (data) => {
        if (this.handlers[event]) this.handlers[event](data);
      });
    }

    this.socket.on('connect', () => {
      this.isConnected = true;
      if (this.handlers.connect) this.handlers.connect();
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      if (this.handlers.disconnect) this.handlers.disconnect(reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('[ScribeSocket] Connection error:', error.message);
      if (this.handlers.error) this.handlers.error(error.message);
    });

    return this;
  }

  on(event, handler) {
    this.handlers[event] = handler;
    return this;
  }

  initScribe({ appointmentId, patientId, practiceId, specialty, noteTemplate, consentConfirmed }) {
    if (!this.socket) {
      console.error('[ScribeSocket] initScribe called but socket is null — connect() may not have been called or token is missing');
      if (this.handlers.scribeError) this.handlers.scribeError({ message: 'Socket not connected. Please refresh and try again.' });
      return;
    }
    if (!this.isConnected) {
      console.warn('[ScribeSocket] initScribe called but socket is not yet connected — waiting...');
    }
    console.log('[ScribeSocket] Emitting initScribe', { patientId, practiceId, specialty, noteTemplate });
    this.socket.emit('initScribe', { appointmentId, patientId, practiceId, specialty, noteTemplate, consentConfirmed: consentConfirmed === true });
  }

  startAudio() {
    if (!this.socket) return;
    this.socket.emit('scribeAudioStart');
  }

  /** What the mic actually delivered (rate/format/path) — logged server-side against the session (GH-184). */
  reportAudioSource(source) {
    if (!this.socket) return;
    this.socket.emit('scribeAudioSource', source);
  }

  sendAudio(base64Audio) {
    if (!this.socket) return;
    this.socket.emit('scribeAudioInput', { audio: base64Audio });
  }

  pause() {
    if (!this.socket) return;
    this.socket.emit('scribePause');
  }

  resume() {
    if (!this.socket) return;
    this.socket.emit('scribeResume');
  }

  endSession() {
    if (!this.socket) return;
    this.socket.emit('scribeEnd');
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnected = false;
    this.handlers = {};
  }
}
